// pfs-mfa-settings — Post-Login MFA-Self-Service-Verwaltung (PFS-560, M1.6).
//
// Das wiederverwendbare Settings-Panel, das ein eingeloggter Nutzer freiwillig
// im Konto-/Identity-Bereich aufruft. Spricht die geteilten pfs-auth-Endpoints
// /api/mfa/* (PFS-555):
//   GET    {apiBase}/api/mfa/factors                    — Liste + {required}
//   POST   {apiBase}/api/mfa/factors/{type}/enroll      — Faktor anlegen
//   POST   {apiBase}/api/mfa/factors/{factorId}/confirm — TOTP bestätigen
//   DELETE {apiBase}/api/mfa/factors/{factorId}         — Faktor entfernen
//
// Funktionen: Status sehen · Faktoren auflisten/hinzufügen/entfernen (mehrere
// Geräte) · Recovery-Codes neu erzeugen · MFA deaktivieren (NUR wenn der Tenant
// MFA nicht erzwingt — `required` aus der factors-Response, PFS-559/PFS-560).
//
// Abgrenzung: <pfs-mfa-enroll>/<pfs-mfa-challenge> sind die Login-ZEIT-WCs.
// Dieses WC ist die POST-Login-Verwaltung. Auth: Cookie über den Host-Proxy
// (apiFetch credentials:include) — wie die übrigen MFA-WCs.
//
// Konfiguration (data-*):
//   data-api-base     optionaler Override des API-Base (Default: Context-Source "apiBase").
//   data-factor-types Komma-Liste anbietbarer Typen. Default "totp,recovery".
//   data-issuer       Anzeige-Name in der Authenticator-App (otpauth issuer).
//   data-download-filename  Dateiname für den Recovery-Codes-Download (Default
//                     "recovery-codes.txt"). Die nutzende App setzt z.B.
//                     "mednet-recovery-codes.txt".
//
// Events:
//   pfs:mfa-factors-changed (bubbles, composed) — nach Anlegen/Bestätigen/Löschen/Deaktivieren.
//
// Texte sind vorerst hart (DE) — wie die übrigen MFA-WCs; i18n folgt plattformweit.

import { defineComponent } from "../../composition/define-component.js";
import "../badge/pfs-badge.js";
import "../loading-indicator/pfs-loading-indicator.js";
import "../card-header/pfs-card-header.js";
import "../layout/pfs-card.js";
import "../card-body/pfs-card-body.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { BUTTON_CSS } from "../../styles/button.js";
import { CONTROL_CSS } from "../../styles/control.js";
import { apiFetch, ApiError } from "../../store/api-fetch.js";
import { confirmDialog } from "../confirm-dialog/pfs-confirm-dialog.js";
import qrcode from "qrcode-generator";

// qrSvg rendert eine otpauth://-URI als scanbares QR-SVG (lokal erzeugt).
function qrSvg(uri: string): string {
  try {
    const qr = qrcode(0, "M");
    qr.addData(uri);
    qr.make();
    return qr.createSvgTag({ cellSize: 5, margin: 2, scalable: true });
  } catch {
    return "";
  }
}

function esc(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string),
  );
}

function fmtDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString("de-DE", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return s;
  }
}

interface FactorView {
  id: string;
  type: string;
  status: string;
  label?: string;
  createdAt: string;
  lastUsedAt?: string;
}

interface EnrollResult {
  factorId: string;
  type: string;
  status: string;
  display: Record<string, unknown>;
}

type View =
  | { kind: "list" }
  | { kind: "totp-confirm"; factorId: string; otpauthUri: string; secret: string }
  | { kind: "recovery-show"; codes: string[] };

interface SettingsHost extends HTMLElement {
  _factors?: FactorView[];
  _required?: boolean; // Tenant erzwingt MFA → "Deaktivieren" ausgeblendet
  _view?: View;
  _loading?: boolean;
  _busy?: boolean; // async Aktion läuft (Trigger gesperrt + Spinner) — Plattform-Kanon
  _error?: string;
}

const TYPE_NAMES: Record<string, string> = {
  totp: "Authenticator-App",
  recovery: "Recovery-Codes",
};

// Inline-SVG-Icons (currentColor) — Header + je Faktor-Typ. Bewusst schlank.
const ICON_SHIELD = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3l7 3v6c0 4.4-3 7.5-7 9-4-1.5-7-4.6-7-9V6l7-3z"/><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4"/></svg>`;
const ICON_PHONE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><rect x="6" y="3" width="12" height="18" rx="2"/><path stroke-linecap="round" d="M10 18h4"/></svg>`;
const ICON_KEY = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path stroke-linecap="round" stroke-linejoin="round" d="M15 7a4 4 0 10-3.9 4.9L7 16v3h3l.5-.5H13v-2h2l1.4-1.4A4 4 0 0015 7z"/></svg>`;

function typeIcon(type: string): string {
  return type === "recovery" ? ICON_KEY : ICON_PHONE;
}

// Card-Subtitle (früher das lose <p class="intro">) — lebt jetzt uniform im
// <pfs-card-header data-subtitle> (PFS-761), Loading- + List-State teilen ihn.
const MFA_SUBTITLE = "Ein zweiter Faktor schützt das Konto zusätzlich zum Kennwort.";

// Icon als slotbares <svg slot="icon"> fürs <pfs-card-header> ausgeben.
function slotIcon(icon: string): string {
  return icon.replace("<svg ", `<svg slot="icon" `);
}

// Header = geteilter <pfs-card-header> als DIREKTES Kind der sektionierten
// <pfs-card data-sectioned> (PFS-761/PFS-756): die Card erkennt ihn automatisch,
// schiebt ihn ins Header-Band und trägt den voll-breiten Divider — kein lokales
// .hband-Band mehr. subtitle = gedämpfte Beschreibungszeile unter dem Titel;
// extra = rechtsbündiger Slot-Inhalt (z.B. Status-Pill).
function cardHeader(title: string, subtitle: string, icon: string, extra = ""): string {
  return `<pfs-card-header data-title="${esc(title)}"${subtitle ? ` data-subtitle="${esc(subtitle)}"` : ""}>${slotIcon(icon)}${extra}</pfs-card-header>`;
}

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; color: var(--pfs-color-on-surface); font-size: 0.9rem; }
/* Card-Struktur (Surface/Border/Divider/Padding) + Header-Band + Body-Zone
   liefert die sektionierte <pfs-card data-sectioned> + <pfs-card-header> +
   <pfs-card-body> (PFS-761). Kein lokales .card/.hband/.bd mehr. */
${BUTTON_CSS}
${CONTROL_CSS}
.muted { color: var(--pfs-color-on-surface-muted); }
.error { color: var(--pfs-color-on-danger-container); margin: 0; font-size: 0.85rem; }

/* Status-Pill (in den actions-Slot des <pfs-card-header> geslottet). */
.pill { display: inline-flex; align-items: center; gap: 6px; font-size: 0.72rem; font-weight: 600; padding: 4px 10px; border-radius: 999px; border: 1px solid transparent; }
.pill.on { color: var(--pfs-color-on-success-container); background: color-mix(in srgb, var(--pfs-color-success) 12%, transparent); border-color: color-mix(in srgb, var(--pfs-color-success) 30%, transparent); }
.pill.off { color: var(--pfs-color-on-surface-muted); background: var(--pfs-color-surface-sunken); border-color: var(--pfs-color-outline); }

.required { margin: 0; font-size: 0.8rem; color: var(--pfs-color-on-surface-muted); }

/* Faktor-Zeile: Icon + Meta + Badge/Aktion */
.factors { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.6rem; }
.factor { display: flex; align-items: center; gap: 0.85rem; padding: 0.75rem 0.85rem; border: 1px solid var(--pfs-color-outline); border-radius: 0.8rem; background: var(--pfs-color-surface-sunken); }
.factor .fi { display: grid; place-items: center; height: 40px; width: 40px; border-radius: 0.65rem; flex: none; color: var(--pfs-color-primary); background: var(--pfs-color-surface); border: 1px solid var(--pfs-color-outline); }
.factor .fi svg { width: 19px; height: 19px; }
.factor .fm { min-width: 0; display: flex; flex-direction: column; gap: 0.12rem; }
.factor .fm .n { font-weight: 600; }
.factor .fm .s { font-size: 0.78rem; color: var(--pfs-color-on-surface-subtle); }
.factor .fa { margin-left: auto; display: flex; align-items: center; gap: 0.6rem; }

/* Methode hinzufügen — Kacheln (Add = kein literales '+') */
.addhead { margin: 0 0 0.55rem; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--pfs-color-on-surface-subtle); }
.methods { display: grid; gap: 0.7rem; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); }
.method { display: flex; align-items: center; gap: 0.75rem; padding: 0.8rem; border: 1px dashed var(--pfs-color-outline-strong); border-radius: 0.8rem; background: transparent; text-align: left; cursor: pointer; font: inherit; color: inherit; transition: border-color 0.14s, background 0.14s; }
.method:hover:not(:disabled) { border-style: solid; border-color: var(--pfs-color-primary); background: color-mix(in srgb, var(--pfs-color-primary) 6%, transparent); }
.method:disabled { cursor: default; opacity: 0.6; }
.method .mi { display: grid; place-items: center; height: 38px; width: 38px; border-radius: 0.65rem; flex: none; color: var(--pfs-color-primary); background: color-mix(in srgb, var(--pfs-color-primary) 12%, transparent); }
.method .mi svg { width: 18px; height: 18px; }
.method .mm { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.method .mm .n { font-weight: 600; font-size: 0.87rem; }
.method .mm .s { font-size: 0.75rem; color: var(--pfs-color-on-surface-muted); }
.method .plus { margin-left: auto; color: var(--pfs-color-primary); font-size: 1.25rem; font-weight: 700; line-height: 1; }

/* Enroll: QR (IMMER weiße Card — Dark-Scan-Fix, PFS-734) + Schritte */
.enroll { display: grid; grid-template-columns: auto 1fr; gap: 1.4rem; align-items: start; }
@media (max-width: 520px) { .enroll { grid-template-columns: 1fr; justify-items: center; } }
.qrbox { padding: 0.85rem; border: 1px solid var(--pfs-color-outline); border-radius: 0.9rem; background: var(--pfs-color-qr-canvas); }
.qrbox svg { display: block; width: 11rem; height: 11rem; }
.steps { display: flex; flex-direction: column; gap: 1rem; }
.step { display: flex; gap: 0.75rem; }
.step .num { flex: none; height: 26px; width: 26px; border-radius: 50%; display: grid; place-items: center; font-size: 0.8rem; font-weight: 700; background: var(--pfs-color-primary); color: var(--pfs-color-on-primary); }
.step .sc { display: flex; flex-direction: column; gap: 0.5rem; min-width: 0; }
.step .sc .t { font-weight: 600; }
.step .sc .d { font-size: 0.82rem; color: var(--pfs-color-on-surface-muted); }
.secret { font-family: ui-monospace, monospace; word-break: break-all; font-size: 0.85rem; background: var(--pfs-color-surface-sunken); border: 1px solid var(--pfs-color-outline); border-radius: 0.5rem; padding: 0.5rem 0.6rem; }
.code-input { letter-spacing: 0.3em; text-align: center; font-family: ui-monospace, monospace; max-width: 11rem; }
details summary { cursor: pointer; color: var(--pfs-color-on-surface-muted); font-size: 0.82rem; }

/* Recovery-Codes */
.codes { list-style: none; margin: 0; padding: 0.9rem; background: var(--pfs-color-surface-sunken); border: 1px solid var(--pfs-color-outline); border-radius: 0.6rem; display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.35rem; font-family: ui-monospace, monospace; }
.warn { display: flex; gap: 0.6rem; align-items: flex-start; font-size: 0.82rem; color: var(--pfs-color-badge-fg); background: var(--pfs-color-badge-bg); border: 1px solid color-mix(in srgb, var(--pfs-color-badge-fg) 25%, transparent); border-radius: 0.7rem; padding: 0.7rem 0.85rem; }

.actions { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; }
.footer { border-top: 1px solid var(--pfs-color-outline); padding-top: 0.9rem; margin-top: 0.15rem; }
.spin { display:inline-block; width:0.9em; height:0.9em; margin-right:0.45em; vertical-align:-0.1em; border:2px solid currentColor; border-top-color:transparent; border-radius:50%; animation: pfs-spin 0.7s linear infinite; }
@keyframes pfs-spin { to { transform: rotate(360deg); } }

/* Zeilen-Aktionen = kanonische .btn.sm (PFS-731): Erneuern=secondary, Entfernen=danger. */
.btn:disabled { opacity: 0.55; cursor: default; }

/* Recovery: Bestätigungs-Checkbox gated den Fertig-Button + Kopiert-Hinweis. */
.ackrow { display: flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; color: var(--pfs-color-on-surface-muted); cursor: pointer; }
.ackrow input { width: 1rem; height: 1rem; accent-color: var(--pfs-color-primary); flex: none; }
.copied { align-self: center; font-size: 0.8rem; font-weight: 600; color: var(--pfs-color-on-success-container); }
`;

function factorTypes(host: HTMLElement): string[] {
  const raw = host.getAttribute("data-factor-types");
  if (!raw) return ["totp", "recovery"];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

function typeName(type: string): string {
  return TYPE_NAMES[type] ?? type;
}

function errMsg(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.detail ?? fallback;
  return fallback;
}

defineComponent({
  tagName: "pfs-mfa-settings",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const h = host as SettingsHost;
    if (!h._view) h._view = { kind: "list" };

    const types = factorTypes(host);
    const issuer = host.getAttribute("data-issuer") ?? "";
    // Loader-Größe konfigurierbar (data-loading-variant); Default "main" (der
    // große, den die übrigen Cards nutzen) — konsistent statt ad-hoc "inline".
    const loadingVariant = host.getAttribute("data-loading-variant") || "main";

    const setState = (
      patch: Partial<Pick<SettingsHost, "_factors" | "_required" | "_view" | "_loading" | "_busy" | "_error">>,
    ): void => {
      Object.assign(h, patch);
      paint();
    };

    const emitChanged = (): void => {
      host.dispatchEvent(new CustomEvent("pfs:mfa-factors-changed", { bubbles: true, composed: true }));
    };

    const loadFactors = async (): Promise<void> => {
      setState({ _loading: true, _error: undefined });
      try {
        const res = await apiFetch<{ factors: FactorView[]; required: boolean }>(`/api/mfa/factors`);
        setState({ _factors: res.factors ?? [], _required: !!res.required, _loading: false, _busy: false });
      } catch (e) {
        setState({ _loading: false, _busy: false, _error: errMsg(e, "Faktoren konnten nicht geladen werden.") });
      }
    };

    const enroll = async (type: string): Promise<void> => {
      setState({ _busy: true, _error: undefined });
      try {
        const reqBody: Record<string, unknown> = {};
        if (issuer) reqBody.issuer = issuer;
        const res = await apiFetch<EnrollResult>(`/api/mfa/factors/${encodeURIComponent(type)}/enroll`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(reqBody),
        });
        const d = res.display ?? {};
        if (type === "totp") {
          h._view = {
            kind: "totp-confirm",
            factorId: res.factorId,
            otpauthUri: String(d.otpauthUri ?? d.uri ?? ""),
            secret: String(d.secret ?? ""),
          };
          h._loading = false;
          h._busy = false;
          paint();
        } else if (Array.isArray(d.codes)) {
          h._busy = false;
          h._view = { kind: "recovery-show", codes: d.codes as string[] };
          paint();
          emitChanged();
        } else {
          await loadFactors();
        }
      } catch (e) {
        setState({ _loading: false, _busy: false, _error: errMsg(e, "Anlegen fehlgeschlagen.") });
      }
    };

    const confirmTotp = async (factorId: string, code: string): Promise<void> => {
      setState({ _busy: true, _error: undefined });
      try {
        await apiFetch(`/api/mfa/factors/${encodeURIComponent(factorId)}/confirm`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ response: code }),
        });
        h._view = { kind: "list" };
        emitChanged();
        await loadFactors();
      } catch (e) {
        setState({ _busy: false, _error: errMsg(e, "Bestätigung fehlgeschlagen — Code prüfen.") });
      }
    };

    const removeFactor = async (f: FactorView): Promise<void> => {
      const ok = await confirmDialog({
        title: "Faktor entfernen?",
        message: `Der Faktor „${f.label || typeName(f.type)}" wird entfernt. Ohne zweiten Faktor sinkt die Kontosicherheit.`,
        confirmLabel: "Entfernen",
        variant: "danger",
      });
      if (!ok) return;
      setState({ _busy: true, _error: undefined });
      try {
        await apiFetch(`/api/mfa/factors/${encodeURIComponent(f.id)}`, { method: "DELETE" });
        emitChanged();
        await loadFactors();
      } catch (e) {
        // Idempotent: 404 = Faktor bereits weg (z.B. Doppel-Delete) → Zielzustand
        // erreicht, kein Fehler; Liste neu laden. (PFS-623 Teil 2.)
        if (e instanceof ApiError && e.status === 404) {
          emitChanged();
          await loadFactors();
          return;
        }
        setState({ _busy: false, _error: errMsg(e, "Entfernen fehlgeschlagen.") });
      }
    };

    // Recovery-Codes neu erzeugen = vorhandenen Recovery-Faktor ersetzen (es gibt
    // keinen dedizierten Regenerate-Endpoint; ein Recovery-Faktor IST der Code-Satz).
    //
    // CONSTRUCTIVE BEFORE DESTRUCTIVE: erst den NEUEN Satz anlegen, NUR bei Erfolg
    // den alten löschen. pfs-auth nutzt keine Transaktionen → ein fehlgeschlagenes
    // Anlegen (z.B. Timeout) DARF nie die noch gültigen Codes kosten. Das Backend
    // erzwingt keine Recovery-Uniqueness → ein transienter zweiter Faktor ist ok;
    // das abschließende Löschen ist best-effort (verbleibt der alte, ist das
    // harmlos — kein Datenverlust, nur ein Faktor zu viel; loadFactors zeigt's).
    const regenerateRecovery = async (): Promise<void> => {
      const existing = (h._factors ?? []).find((f) => f.type === "recovery");
      const ok = await confirmDialog({
        title: "Recovery-Codes neu erzeugen?",
        message: "Die bisherigen Recovery-Codes werden ungültig. Du erhältst einen neuen Satz, den du sicher aufbewahren musst.",
        confirmLabel: "Neu erzeugen",
        variant: "danger",
      });
      if (!ok) return;
      setState({ _busy: true, _error: undefined });

      // 1. NEU zuerst — wirft das, bleibt der alte Faktor unangetastet.
      let codes: string[] | undefined;
      try {
        const reqBody: Record<string, unknown> = {};
        if (issuer) reqBody.issuer = issuer;
        const res = await apiFetch<EnrollResult>(`/api/mfa/factors/recovery/enroll`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(reqBody),
        });
        const d = res.display ?? {};
        if (Array.isArray(d.codes)) codes = d.codes as string[];
      } catch (e) {
        // Anlegen scheiterte → die bisherigen Codes sind UNVERÄNDERT gültig.
        setState({ _busy: false, _error: errMsg(e, "Neu erzeugen fehlgeschlagen — die bisherigen Codes bleiben gültig.") });
        return;
      }

      // 2. Erst JETZT den alten Satz entwerten — best-effort, Fehler nicht fatal.
      if (existing) {
        try {
          await apiFetch(`/api/mfa/factors/${encodeURIComponent(existing.id)}`, { method: "DELETE" });
        } catch {
          /* alter Satz bleibt — kein Datenverlust, nur ein Faktor zu viel */
        }
      }
      emitChanged();

      // 3. neue Codes anzeigen (oder Liste neu laden, falls keine kamen).
      if (codes) {
        h._busy = false;
        h._view = { kind: "recovery-show", codes };
        paint();
      } else {
        await loadFactors();
      }
    };

    // MFA deaktivieren = alle Faktoren entfernen. Recovery/Backup ZUERST löschen,
    // sonst lehnt das Backend ab (ein Primärfaktor darf nicht so gelöscht werden,
    // dass nur Backup-Faktoren übrig bleiben — leavesBackupOnly).
    const disableMfa = async (): Promise<void> => {
      const ok = await confirmDialog({
        title: "Zwei-Faktor-Authentifizierung deaktivieren?",
        message: "Alle Faktoren werden entfernt. Dein Konto ist dann nur noch durch das Passwort geschützt.",
        confirmLabel: "Deaktivieren",
        variant: "danger",
      });
      if (!ok) return;
      setState({ _busy: true, _error: undefined });
      try {
        const ordered = [...(h._factors ?? [])].sort(
          (a, b) => Number(b.type === "recovery") - Number(a.type === "recovery"),
        );
        for (const f of ordered) {
          await apiFetch(`/api/mfa/factors/${encodeURIComponent(f.id)}`, { method: "DELETE" });
        }
        emitChanged();
        await loadFactors();
      } catch (e) {
        setState({ _busy: false, _error: errMsg(e, "Deaktivieren fehlgeschlagen — evtl. erzwingt der Tenant MFA.") });
      }
    };

    function factorRow(f: FactorView): string {
      const badgeVariant = f.status === "active" ? "success" : f.status === "pending" ? "warning" : "neutral";
      const statusLabel = f.status === "active" ? "Aktiv" : f.status === "pending" ? "Ausstehend" : f.status;
      const sub = f.lastUsedAt ? `zuletzt genutzt ${esc(fmtDate(f.lastUsedAt))}` : `erstellt ${esc(fmtDate(f.createdAt))}`;
      const action =
        f.type === "recovery"
          ? `<button class="btn secondary sm" data-regen ${h._busy ? "disabled" : ""}>Erneuern</button>`
          : `<button class="btn danger sm" data-del="${esc(f.id)}" ${h._busy ? "disabled" : ""}>Entfernen</button>`;
      return `
        <li class="factor">
          <span class="fi">${typeIcon(f.type)}</span>
          <span class="fm">
            <span class="n">${esc(f.label || typeName(f.type))}</span>
            <span class="s">${esc(typeName(f.type))} · ${sub}</span>
          </span>
          <span class="fa"><pfs-badge data-variant="${badgeVariant}">${esc(statusLabel)}</pfs-badge>${action}</span>
        </li>`;
    }

    function methodDesc(t: string): string {
      if (t === "totp") return "Zeitbasierter Code aus einer App";
      if (t === "recovery") return "Einmal-Codes für den Notfall";
      return "";
    }

    function listView(): string {
      if (h._loading) {
        return `
          ${cardHeader("Zwei-Faktor-Authentifizierung", MFA_SUBTITLE, ICON_SHIELD)}
          <pfs-card-body><pfs-loading-indicator data-variant="${loadingVariant}" data-text="Faktoren werden geladen"></pfs-loading-indicator></pfs-card-body>`;
      }
      const factors = h._factors ?? [];
      const active = factors.filter((f) => f.status === "active");
      const hasRecovery = factors.some((f) => f.type === "recovery");
      const enabled = active.length > 0;
      const pill = enabled ? `<span class="pill on">● Aktiv</span>` : `<span class="pill off">○ Inaktiv</span>`;
      const requiredHint = h._required
        ? `<p class="required">Zwei-Faktor-Authentifizierung wird in dieser Umgebung verlangt und kann nicht deaktiviert werden.</p>`
        : "";
      const list = factors.length
        ? `<ul class="factors">${factors.map(factorRow).join("")}</ul>`
        : `<p class="muted">Noch kein zweiter Faktor eingerichtet.</p>`;
      const methodCards = types
        .map((t) => {
          if (t === "recovery" && hasRecovery) return ""; // Erneuern läuft über die Zeile
          return `<button class="method" data-add="${esc(t)}" ${h._busy ? "disabled" : ""}>
            <span class="mi">${typeIcon(t)}</span>
            <span class="mm"><span class="n">${esc(typeName(t))}</span><span class="s">${esc(methodDesc(t))}</span></span>
            <span class="plus">${h._busy ? `<span class="spin" aria-hidden="true"></span>` : "+"}</span>
          </button>`;
        })
        .filter(Boolean)
        .join("");
      const addBlock = methodCards
        ? `<div><p class="addhead">Methode hinzufügen</p><div class="methods">${methodCards}</div></div>`
        : "";
      const disableBtn =
        enabled && !h._required
          ? `<div class="actions footer"><button class="btn danger" data-disable ${h._busy ? "disabled" : ""}>Zwei-Faktor-Authentifizierung deaktivieren</button></div>`
          : "";
      return `
        ${cardHeader("Zwei-Faktor-Authentifizierung", MFA_SUBTITLE, ICON_SHIELD, `<span slot="header-right">${pill}</span>`)}
        <pfs-card-body>
          ${requiredHint}
          ${h._error ? `<p class="error">${esc(h._error)}</p>` : ""}
          ${list}
          ${addBlock}
          ${disableBtn}
        </pfs-card-body>`;
    }

    function paint(): void {
      if (!root) return;
      let inner = "";
      const view = h._view ?? { kind: "list" };
      if (view.kind === "totp-confirm") {
        const manual = view.secret
          ? `<div class="secret">${esc(view.secret)}</div>`
          : view.otpauthUri
            ? `<details><summary>otpauth-URI anzeigen</summary><div class="secret" data-otpauth>${esc(view.otpauthUri)}</div></details>`
            : "";
        inner = `
          ${cardHeader("Authenticator-App einrichten", "Scanne den QR-Code mit deiner Authenticator-App.", ICON_PHONE)}
          <pfs-card-body>
            <div class="enroll">
              ${view.otpauthUri ? `<div class="qrbox" aria-label="QR-Code zum Scannen">${qrSvg(view.otpauthUri)}</div>` : `<div></div>`}
              <div class="steps">
                <div class="step">
                  <span class="num">1</span>
                  <div class="sc">
                    <span class="t">QR-Code scannen</span>
                    <span class="d">QR-Code mit einer Authenticator-App scannen — oder den Schlüssel manuell eintragen.</span>
                    ${manual}
                  </div>
                </div>
                <div class="step">
                  <span class="num">2</span>
                  <div class="sc">
                    <span class="t">Code bestätigen</span>
                    <span class="d">Den aktuell angezeigten 6-stelligen Code eingeben.</span>
                    <input class="code-input" type="text" inputmode="numeric" autocomplete="one-time-code" placeholder="123456" data-confirm-code ${h._busy ? "disabled" : ""} />
                  </div>
                </div>
              </div>
            </div>
            ${h._error ? `<p class="error">${esc(h._error)}</p>` : ""}
            <div class="actions">
              <button class="btn secondary" data-cancel ${h._busy ? "disabled" : ""}>Abbrechen</button>
              <button class="btn primary" data-do-confirm ${h._busy ? "disabled" : ""}>${h._busy ? `<span class="spin" aria-hidden="true"></span>Prüfe…` : "Aktivieren"}</button>
            </div>
          </pfs-card-body>`;
      } else if (view.kind === "recovery-show") {
        inner = `
          ${cardHeader("Recovery-Codes", "Einmal-Codes für den Notfall — jetzt sicher aufbewahren.", ICON_KEY)}
          <pfs-card-body>
            <div class="warn"><span aria-hidden="true">⚠</span><span>Diese Einmal-Codes sicher aufbewahren — sie werden nur jetzt angezeigt. Jeder Code funktioniert genau einmal.</span></div>
            <ul class="codes">${view.codes.map((c) => `<li>${esc(c)}</li>`).join("")}</ul>
            <div class="actions">
              <button class="btn secondary" data-download>Herunterladen</button>
              <button class="btn secondary" data-copy>Kopieren</button>
              <span class="copied" data-copied hidden>Kopiert ✓</span>
            </div>
            <label class="ackrow"><input type="checkbox" data-ack-check /> Ich habe die Codes sicher gespeichert.</label>
            <div class="actions"><button class="btn secondary" data-back>Zurück</button><button class="btn primary" data-ack disabled>Fertig</button></div>
          </pfs-card-body>`;
      } else {
        inner = listView();
      }
      root.innerHTML = `<style>${STYLES}</style><pfs-card data-sectioned>${inner}</pfs-card>`;
      wire();
    }

    function wire(): void {
      if (!root) return;
      root.querySelectorAll<HTMLElement>("[data-add]").forEach((b) =>
        b.addEventListener("click", () => void enroll(b.getAttribute("data-add") ?? "")),
      );
      root.querySelectorAll<HTMLElement>("[data-del]").forEach((b) =>
        b.addEventListener("click", () => {
          const id = b.getAttribute("data-del") ?? "";
          const f = (h._factors ?? []).find((x) => x.id === id);
          if (f) void removeFactor(f);
        }),
      );
      root.querySelector("[data-regen]")?.addEventListener("click", () => void regenerateRecovery());
      root.querySelector("[data-disable]")?.addEventListener("click", () => void disableMfa());
      root.querySelector("[data-cancel]")?.addEventListener("click", () => {
        h._view = { kind: "list" };
        h._error = undefined;
        paint();
      });
      const doConfirm = (): void => {
        const v = h._view;
        const input = root.querySelector<HTMLInputElement>("[data-confirm-code]");
        if (v && v.kind === "totp-confirm" && input) void confirmTotp(v.factorId, input.value.trim());
      };
      root.querySelector("[data-do-confirm]")?.addEventListener("click", doConfirm);
      root.querySelector<HTMLInputElement>("[data-confirm-code]")?.addEventListener("keydown", (e) => {
        if ((e as KeyboardEvent).key === "Enter") doConfirm();
      });
      root.querySelector("[data-ack]")?.addEventListener("click", () => {
        h._view = { kind: "list" };
        void loadFactors();
      });
      // „Zurück" ist immer aktiv (kein Gate) — sonst sitzt man in der Recovery-
      // Ansicht fest, solange man die Codes (noch) nicht bestätigt hat, und
      // kommt nie zur Faktoren-Liste / Authenticator-Einrichtung zurück.
      root.querySelector("[data-back]")?.addEventListener("click", () => {
        h._view = { kind: "list" };
        void loadFactors();
      });
      // Recovery-Codes: Herunterladen / Kopieren + Bestätigungs-Gate (der
      // Fertig-Button ist disabled, bis die Checkbox bestätigt ist).
      const recoveryCodesText = (): string => {
        const v = h._view;
        return v && v.kind === "recovery-show" ? v.codes.join("\n") : "";
      };
      root.querySelector("[data-download]")?.addEventListener("click", () => {
        const blob = new Blob([recoveryCodesText() + "\n"], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        // Dateiname von der nutzenden App konfigurierbar (data-download-filename),
        // damit z.B. mednet nicht „pfs-…" zu sehen bekommt. Neutraler Default.
        a.download = host.getAttribute("data-download-filename") || "recovery-codes.txt";
        a.click();
        URL.revokeObjectURL(url);
      });
      root.querySelector("[data-copy]")?.addEventListener("click", () => {
        void navigator.clipboard?.writeText(recoveryCodesText());
        const c = root.querySelector<HTMLElement>("[data-copied]");
        if (c) c.hidden = false;
      });
      const ackCheck = root.querySelector<HTMLInputElement>("[data-ack-check]");
      const ackBtn = root.querySelector<HTMLButtonElement>("[data-ack]");
      ackCheck?.addEventListener("change", () => {
        if (ackBtn) ackBtn.disabled = !ackCheck.checked;
      });
    }

    paint();
    void loadFactors();
  },
});
